import Button from '@/components/atoms/Button'
import Icon from '@/components/atoms/Icon'
import Modal from '@/components/atoms/Modal'
import Raters from '@/components/atoms/Raters'
import Select from '@/components/atoms/Select'
import Textarea from '@/components/atoms/Textarea'
import { modalsRepository } from '@/repositories'
import { feedbackMentorshipSchema } from '@/schemas/mentorship.schema'
import mentorshipsService from '@/services/mentorshipsService'
import { modalCreators } from '@/store/ducks/modal'
import { notificationCreators } from '@/store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import inputIsValid from '@/utils/helpers/inputIsValid'
import { Dialog } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'

interface StudentFeedbackProps {
	disabledOnClose?: boolean
}

type Inputs = {
	feedback?: string
	reasonOther?: string
}

const options = [
	{ value: 'MENTOR_DID_NOT_ATTEND', label: 'Mentor(a) não compareceu' },
	{ value: 'STUDENT_DID_NOT_ATTEND', label: 'Eu não compareci' },
	{ value: 'RESCHEDULED', label: 'Decidimos remarcar' },
	{ value: 'OTHER', label: 'Outros' },
]

export function StudentFeedback({ disabledOnClose }: StudentFeedbackProps) {
	const { modalInfo: mentorship } = useAppSelector((state) => state.modal)

	const dispatch = useAppDispatch()

	const [happened, setHappened] = useState(true)
	const [rating, setRating] = useState(0)
	const [minLenght, setMinLenght] = useState(true)

	const [selectReason, setSelectReason] = useState<{
		label?: string
		value?: string
	}>({})

	const {
		execute: feedbackMentorship,
		isLoading,
		isSuccess,
	} = mentorshipsService.feedbackMentorship()

	const {
		register,
		handleSubmit,
		formState: { errors, touchedFields },
		control,
		reset,
	} = useForm<Inputs>({
		mode: 'onBlur',
		context: { isOtherSelect: selectReason?.value === 'OTHER' },
		resolver: yupResolver(feedbackMentorshipSchema),
	})

	const onSubmit = (data: Inputs) => {
		if (!mentorship?.id) {
			return
		}

		const newData = {
			mentorshipId: mentorship?.id,
			feedback: happened && data?.feedback ? data.feedback : null,
			rating: happened ? rating : null,
			reason: !happened ? selectReason.value : null,
			reasonOther:
				!happened && selectReason?.value === 'OTHER' ? data.reasonOther : null,
			happened,
			type: 'STUDENT',
		}

		feedbackMentorship(newData)
	}

	useEffect(() => {
		if (isSuccess) {
			setHappened(true)
			setRating(0)
			setMinLenght(true)
			setSelectReason({})
			dispatch(modalCreators.closeModal())
			reset()
			dispatch(
				notificationCreators.show({
					title: 'Enviado',
					message: 'Seu feeback foi enviado com sucesso.',
				})
			)
			dispatch(modalsRepository.util.invalidateTags(['Modals']))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccess])

	return (
		<Modal
			name='studentMentorshipFeedbackModal'
			disabledOnClose={disabledOnClose}
			className='md:p-8 max-w-[650px]'
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className='flex flex-col gap-6'>
					<div className='flex flex-col gap-3'>
						<Dialog.Title
							as='h3'
							className='text-start text-2xl font-medium md:leading-[104%] tracking-tight text-zinc-50'
						>
							{mentorship?.name
								? `Como foi a mentoria com ${mentorship?.name} ${mentorship?.lastName}?`
								: `Como foi a sua mentoria?`}
						</Dialog.Title>
						<p className='text-start w-full text-base font-medium text-white/70 leading-[155%]'>
							Avalie sua sessão recente de mentoria
						</p>
					</div>
					<div className='flex flex-col gap-7'>
						<div className='flex flex-col items-center gap-3'>
							<div className='flex flex-col w-full items-center'>
								{(() => {
									if (mentorship?.profilePicture) {
										return (
											<img
												src={`${process.env.REACT_APP_IMAGE_PROFILE_URL}${mentorship?.profilePicture}`}
												alt='Avatar Mentor'
												className='min-w-[96px] min-h-[96px] max-w-[96px] max-h-[96px] object-fill border-4 xl:border border-gray-100 rounded-full'
											/>
										)
									}

									return (
										<Icon
											name='default-avatar'
											width='52'
											height='52'
											className='min-w-[96px] min-h-[96px] max-w-[96px] max-h-[96px] object-fill border-4 xl:border border-gray-100 rounded-full'
										/>
									)
								})()}
							</div>
							<div className='flex flex-col gap-1'>
								<span className='text-base font-medium text-zinc-50 leading-tight'>{`${mentorship?.name} ${mentorship?.lastName}`}</span>
								<p className='text-base text-zinc-50 leading-tight'>
									Mentoria do dia{' '}
									{dayjs(mentorship?.date).locale('pt-br').format('DD/MM/YYYY')}
								</p>
							</div>
						</div>

						{(() => {
							if (happened) {
								return (
									<div className='flex flex-col gap-8'>
										<div className='flex items-center justify-center'>
											<Raters
												total={5}
												onRate={({ rating }) => {
													setRating(rating)
												}}
											/>
										</div>
									</div>
								)
							}
						})()}

						{(() => {
							if (!rating) {
								return (
									<div className='flex flex-col gap-8'>
										<label className='relative inline-flex flex-col cursor-pointer'>
											<input
												type='checkbox'
												className='sr-only peer'
												checked={happened}
												onChange={() =>
													setHappened((toogle: boolean) => !toogle)
												}
											/>

											<div className='flex items-center gap-2'>
												<span
													className={twMerge(
														'flex w-6 h-6 rounded-full bg-transparent border items-center justify-center',
														classNames({
															'border-2 border-[#eb4755]': !happened,
														})
													)}
												>
													{!happened && (
														<Icon
															name='check'
															width='12px'
															height='12px'
															fill='#eb4755'
														/>
													)}
												</span>
												<p
													className={twMerge(
														'text-base font-medium leading-[150%] text-start text-white/70 select-none',
														classNames({ 'text-[#eb4755]': !happened })
													)}
												>
													A mentoria não aconteceu
												</p>
											</div>
										</label>
									</div>
								)
							}

							return (
								<div className='flex flex-col gap-4'>
									<div className='flex flex-col gap-2'>
										<p className='text-start w-full text-lg text-zinc-50 leading-[104%] gap-2 inline-block items-center'>
											Algo que você gostaria de compartilhar?
										</p>
										<p className='text-start w-full text-base font-medium text-white/70 leading-[115%]'>
											Se puder, deixe um feedback adicional! Vai ser muito
											importante para nós
											<span className='inline-block'>
												<Icon
													name='heart'
													width='20'
													height='20'
													className='ml-2 pt-2 text-white/70 dark:text-[#F7F9FB]'
												/>
											</span>
										</p>
									</div>
									<div className='flex flex-col'>
										<Controller
											control={control}
											name='feedback'
											render={() => (
												<Textarea
													isValid={inputIsValid(
														errors.feedback,
														touchedFields.feedback
													)}
													{...register('feedback')}
													minimalCharacters={rating <= 2 ? 50 : undefined}
													onMinLenght={({ isDisabled }) => {
														setMinLenght(isDisabled)
													}}
													rows={4}
													placeholder='Escreva um comentário...'
												/>
											)}
										/>
									</div>
								</div>
							)
						})()}

						{(() => {
							if (!happened) {
								return (
									<div className='relative w-full'>
										<Select
											selectedField={selectReason}
											setSelectedField={setSelectReason}
											options={options}
											classNameButton='h-[42px] rounded-md'
											classNameOptions='relative rounded-md'
										/>
									</div>
								)
							}
						})()}

						{(() => {
							if (!happened && selectReason.value === 'OTHER') {
								return (
									<div className='flex flex-col relative w-full gap-4'>
										<div className='flex flex-col gap-1'>
											<span className='flex text-start text-base text-zinc-50 leading-tight'>
												Conte-nos o que aconteceu*
											</span>
											<p className='flex text-start text-sm text-white/70 leading-tight'>
												Especifique porque a mentoria não ocorreu.
											</p>
										</div>
										<Controller
											control={control}
											name='reasonOther'
											render={() => (
												<Textarea
													isValid={inputIsValid(
														errors.reasonOther,
														touchedFields.reasonOther
													)}
													{...register('reasonOther')}
													rows={4}
													placeholder='Escreva um comentário...'
												/>
											)}
										/>
									</div>
								)
							}
						})()}

						{(() => {
							if (
								(!happened && selectReason?.value !== 'OTHER') ||
								(selectReason?.value === 'OTHER' &&
									!errors.reasonOther?.message) ||
								(happened && rating)
							) {
								return (
									<Button
										className='w-full rounded-full'
										disabled={happened && rating <= 2 && minLenght}
										isLoading={isLoading}
										type='submit'
									>
										Finalizar
									</Button>
								)
							}
						})()}
					</div>
				</div>
			</form>
		</Modal>
	)
}
